import React, { useState, useMemo, useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import { useSelectableColumns } from '../../common/table/useSelectableColumns';
import { useMetadataList } from '../../documentation/metadata/useMetadataList';
import { useMetadata } from '../../documentation/metadata/useMetadata';
import { getColumns } from '../../documentation/selected-object/expanded-mode/tableColumns';
import { DocumentationTabTypes } from '../../../types/enums/DocumentationTabTypes';
import { VirtualScrollCustomColumnsTable } from '../../common/table/custom-columns-table/VirtualScrollCustomColumnsTable';
import { CustomColumnsTableContextProvider } from '../../common/table/custom-columns-table/CustomColumnsTableContext';
import { TopBarRenderer } from '../../documentation/shared/layouts/TopBarRenderer';
import { MetadataFilters } from '../../documentation/metadata/MetadataFilters';
import { createMetadataSelectionItem, TableEmptyState, SelectableItem } from './utils';
import { SelectionItem } from './AgentContextSelectionModal';
import { DataTableBaseColumn, DataTableRow } from '../../common/table/TableTypes';
import { DataTableId } from '../../common/table/utils';

interface MetadataTableForAgentProps {
  metadataType: string;
  crmOrgId: string;
  onSelectionChange: (selections: SelectionItem[]) => void;
  isSubmitting?: boolean;
  initialSelections?: SelectionItem[];
}

export const MetadataTableForAgent: React.FC<MetadataTableForAgentProps> = ({
  metadataType,
  crmOrgId,
  onSelectionChange,
  isSubmitting = false,
  initialSelections = [],
}) => {
  // Store name-based selections (for consistency with initial data and API)
  const [nameBasedSelections, setNameBasedSelections] = useState<string[]>(() => {
    return initialSelections.map((selection) => selection.name);
  });

  const [isLoading, setIsLoading] = useState(false);
  const { fetchMetadata } = useMetadata();

  // Fetch metadata for the specified type
  const { items: metadataData } = useMetadataList(crmOrgId, metadataType);

  // Convert stored names to row IDs for checkbox display (only in update mode)
  const selectedRowIds = useMemo(() => {
    if (nameBasedSelections.length === 0 || !metadataData) {
      return nameBasedSelections; // No selections or no data yet
    }

    // Convert stored names to row IDs for table display
    const convertedIds: string[] = [];
    nameBasedSelections.forEach((selectionName) => {
      const matchingRow = metadataData.find((row: any) => row.name === selectionName);
      if (matchingRow) {
        const rowId = matchingRow.id || matchingRow.metadataId || matchingRow.name;
        convertedIds.push(rowId);
      }
    });
    return convertedIds.length > 0 ? convertedIds : nameBasedSelections;
  }, [nameBasedSelections, metadataData]);

  useEffect(() => {
    if (metadataType && crmOrgId) {
      setIsLoading(true);

      fetchMetadata(metadataType, crmOrgId).finally(() => {
        setIsLoading(false);
      });
    }
  }, [metadataType, crmOrgId, fetchMetadata]);

  const columns = useMemo(() => {
    return getColumns(DocumentationTabTypes.OTHER); // Use OTHER tab type for metadata
  }, []);

  const handleSelectionChange = (selectedIds: string[]) => {
    // Convert IDs back to names for storage
    const convertedToNames: string[] = [];
    selectedIds.forEach((selectedId) => {
      const matchingRow = metadataData?.find((row: any) => {
        const rowId = row.id || row.metadataId || row.name;
        return rowId === selectedId;
      });
      if (matchingRow && matchingRow.name) {
        convertedToNames.push(matchingRow.name);
      }
    });

    setNameBasedSelections(convertedToNames);

    // Convert names to selection items for parent component
    const newSelections: SelectionItem[] = convertedToNames
      .map((selectionName) => {
        if (!selectionName) {
          return null;
        }
        const item = metadataData?.find((item: SelectableItem) => item.name === selectionName);
        if (item) {
          return createMetadataSelectionItem(item);
        } else {
          return null;
        }
      })
      .filter(Boolean) as SelectionItem[];
    onSelectionChange(newSelections);
  };

  const enhancedColumns = useSelectableColumns(columns, metadataData || [], {
    selectedRowIds,
    onSelectionChange: handleSelectionChange,
  });

  // Handle row click to toggle selection
  const handleRowClick = (
    event: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    row: DataTableRow,
    column: DataTableBaseColumn,
  ) => {
    if (isSubmitting) return;

    // Don't handle clicks on the selection column (checkbox column)
    if (column.field === '__selection') {
      return;
    }

    const rowId = row.id || row.metadataId || row.name;

    if (!rowId) {
      return;
    }

    const isSelected = selectedRowIds.includes(rowId);

    if (isSelected) {
      // Remove from selection
      const newSelection = selectedRowIds.filter((id) => id !== rowId);
      handleSelectionChange(newSelection);
    } else {
      // Add to selection
      const newSelection = [...selectedRowIds, rowId];
      handleSelectionChange(newSelection);
    }
  };

  const topBarProps = useMemo(
    () => ({
      label: '',
      filtersJsx: (
        <MetadataFilters
          key={`agent-selection-metadata-${metadataType}`}
          crmOrgId={crmOrgId}
          activeTab={metadataType}
        />
      ),
      searchPlaceholder: 'Search',
      itemsCount: metadataData?.length || 0,
      showResultsCount: true,
      allowColumnsManipulation: false,
      isExpandedMode: true,
    }),
    [crmOrgId, metadataType, metadataData?.length],
  );

  return (
    <Stack spacing={3} sx={{ height: '100%', flex: 1 }}>
      <TopBarRenderer {...topBarProps} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <CustomColumnsTableContextProvider
          value={{
            tableId: `agent-selection-metadata-${metadataType}` as DataTableId,
            columns: enhancedColumns,
            rows: metadataData || [],
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              overflowX: 'auto',
              '& .SweepDataTableCell': {
                overflow: 'hidden',
                '& .MuiTypography-root': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
            }}
          >
            <VirtualScrollCustomColumnsTable
              tableId={`agent-selection-metadata-${metadataType}` as DataTableId}
              rows={metadataData || []}
              onRowClick={isSubmitting ? undefined : handleRowClick}
              hideChevronButton={true}
              tableEmptyStateJsx={<TableEmptyState />}
              isLoading={isLoading}
            />
          </Box>
        </CustomColumnsTableContextProvider>
      </Box>
    </Stack>
  );
};
